import React from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useCourses } from '@/hooks/useCourses'
import { useUsers } from '@/hooks/useUsers'
import { usePendingWithdrawals } from '@/hooks/useWithdrawal'
import { NotificationList } from '@/components/features/notifications'

interface StatCardProps {
    icon: string
    label: string
    value: string | number
    isLoading?: boolean
    gradient: string
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, isLoading, gradient }) => (
    <div className={`relative overflow-hidden rounded-xl p-6 ${gradient} text-white shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-1 group`}>
        {/* Background Decorative Elements */}
        <div className="absolute top-0 right-0 w-28 h-28 bg-white/10 rounded-full -mr-14 -mt-14 blur-2xl transition-transform group-hover:scale-110 duration-500" />
        <div className="absolute bottom-0 left-0 w-20 h-20 bg-black/5 rounded-full -ml-10 -mb-10 blur-xl" />

        <div className="relative z-10">
            <div className={`inline-flex items-center justify-center size-11 rounded-lg bg-white/20 backdrop-blur-md shadow-[inset_0_1px_1px_rgba(255,255,255,0.4)] mb-4 transition-transform group-hover:rotate-6 duration-300`}>
                <span className="material-symbols-outlined text-white text-[24px] drop-shadow-sm">{icon}</span>
            </div>
            <p className="text-sm font-black text-white/80 mb-1">{label}</p>
            {isLoading ? (
                <div className="animate-pulse bg-white/20 h-8 rounded w-20 mt-1" />
            ) : (
                <p className="text-3xl font-black tracking-tight drop-shadow-md">{value}</p>
            )}
        </div>
    </div>
)

interface QuickActionProps {
    icon: string
    label: string
    description: string
    to: string
    iconGradient: string
}

const QuickAction: React.FC<QuickActionProps> = ({ icon, label, description, to, iconGradient }) => (
    <Link
        to={to}
        className="flex items-start gap-4 p-5 rounded-xl bg-white dark:bg-surface-card border border-slate-100 dark:border-white/5 hover:border-primary/30 hover:shadow-lg transition-all group"
    >
        <div className={`size-11 shrink-0 rounded-lg ${iconGradient} flex items-center justify-center shadow-md group-hover:scale-105 transition-transform duration-300`}>
            <span className="material-symbols-outlined text-white text-[20px]">{icon}</span>
        </div>
        <div className="flex-1 min-w-0">
            <span className="text-sm font-black text-slate-900 dark:text-white block group-hover:text-primary transition-colors tracking-tight">{label}</span>
            <span className="text-xs text-slate-500 dark:text-slate-400 mt-1 block leading-relaxed font-medium">{description}</span>
        </div>
    </Link>
)

export const AdminDashboardPage: React.FC = () => {
    const { user } = useAuth()
    const { data: courses, isLoading: isLoadingCourses } = useCourses()
    const { data: users, isLoading: isLoadingUsers } = useUsers()
    const { data: pendingWithdrawals, isLoading: isLoadingWithdrawals } = usePendingWithdrawals()

    const totalUsers = users?.filter((u: any) => u.role === 'student').length || 0
    const totalEnrollments = courses?.reduce((acc: number, course: any) => acc + (course.enrollmentCount || 0), 0) || 0

    const hour = new Date().getHours()
    const greeting = hour < 11 ? 'Selamat Pagi' : hour < 15 ? 'Selamat Siang' : hour < 18 ? 'Selamat Sore' : 'Selamat Malam'

    return (
        <div className="space-y-8">
            {/* ── Welcome Banner ── */}
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-900 via-slate-800 to-primary/80 dark:from-slate-900 dark:via-slate-800 dark:to-primary/60 p-8 text-white shadow-xl">
                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-primary/30 to-transparent rounded-full blur-3xl" />

                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <p className="text-sm font-black text-white/60 mb-1">{greeting}</p>
                        <h1 className="text-3xl font-black tracking-tight">{user?.name || 'Super Admin'}</h1>
                        <p className="text-white/50 text-sm mt-1 font-bold">Control Panel • Pelatihan Merdeka</p>
                    </div>
                    <div className="flex items-center gap-3 bg-white/5 backdrop-blur-md px-4 py-2.5 rounded-xl border border-white/10">
                        <span className="material-symbols-outlined text-white/60 text-[18px]">calendar_today</span>
                        <p className="text-sm font-black text-white/90">
                            {new Date().toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                    </div>
                </div>
            </div>

            {/* ── Stats Grid ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    icon="school"
                    label="Total Kursus"
                    value={courses?.length || 0}
                    isLoading={isLoadingCourses}
                    gradient="bg-gradient-to-br from-rose-500 to-pink-600"
                />
                <StatCard
                    icon="group"
                    label="Total Peserta"
                    value={totalUsers}
                    isLoading={isLoadingUsers}
                    gradient="bg-gradient-to-br from-blue-500 to-indigo-600"
                />
                <StatCard
                    icon="person_add"
                    label="Pendaftaran"
                    value={totalEnrollments}
                    isLoading={isLoadingCourses}
                    gradient="bg-gradient-to-br from-emerald-500 to-teal-600"
                />
                <StatCard
                    icon="payments"
                    label="Penarikan"
                    value={pendingWithdrawals?.length || 0}
                    isLoading={isLoadingWithdrawals}
                    gradient="bg-gradient-to-br from-amber-500 to-orange-600"
                />
            </div>

            {/* ── Quick Actions ── */}
            <div>
                <div className="flex items-center gap-2 mb-4">
                    <div className="h-5 w-1.5 bg-primary rounded-full" />
                    <h2 className="text-sm font-black text-slate-900 dark:text-white">Aksi Utama</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <QuickAction
                        icon="add_circle"
                        label="Buat Kursus"
                        description="Susun kurikulum baru"
                        to="/admin/courses/new"
                        iconGradient="bg-gradient-to-br from-blue-500 to-indigo-600"
                    />
                    <QuickAction
                        icon="group_add"
                        label="Kelola User"
                        description="Atur akses pengguna"
                        to="/admin/users"
                        iconGradient="bg-gradient-to-br from-purple-500 to-pink-600"
                    />
                    <QuickAction
                        icon="calendar_add_on"
                        label="Buat Batch"
                        description="Jadwalkan pelatihan"
                        to="/admin/batches"
                        iconGradient="bg-gradient-to-br from-emerald-500 to-teal-600"
                    />
                </div>
            </div>

            {/* ── Notifications Section ── */}
            <div className="rounded-xl bg-white dark:bg-surface-card border border-slate-100 dark:border-white/5 overflow-hidden shadow-sm">
                <div className="px-6 py-4 border-b border-slate-100 dark:border-white/5 flex items-center justify-between bg-slate-50/30 dark:bg-white/[0.01]">
                    <div className="flex items-center gap-3">
                        <span className="material-symbols-outlined text-primary text-[22px]">notifications</span>
                        <h3 className="text-sm font-black text-slate-900 dark:text-white">Notifikasi Terbaru</h3>
                    </div>
                </div>
                <div className="p-4">
                    <NotificationList userId={user?.id} limit={5} compact={false} />
                </div>
            </div>

            {/* ── Recent Activity (Side by Side) ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Courses */}
                <div className="rounded-xl bg-white dark:bg-surface-card border border-slate-100 dark:border-white/5 overflow-hidden shadow-sm">
                    <div className="px-6 py-4 border-b border-slate-100 dark:border-white/5 flex items-center justify-between bg-slate-50/30 dark:bg-white/[0.01]">
                        <div className="flex items-center gap-3">
                            <span className="material-symbols-outlined text-primary text-[22px]">rocket_launch</span>
                            <h3 className="text-sm font-black text-slate-900 dark:text-white">Kursus Terbaru</h3>
                        </div>
                        <Link to="/admin/courses" className="text-sm font-black text-primary hover:underline flex items-center gap-1.5">
                            Semua
                            <span className="material-symbols-outlined text-[14px]">arrow_forward</span>
                        </Link>
                    </div>
                    <div className="divide-y divide-slate-50 dark:divide-white/5">
                        {isLoadingCourses ? (
                            <div className="p-10 text-center text-[11px] font-bold text-slate-400">Sinkronisasi...</div>
                        ) : courses && courses.length > 0 ? (
                            courses.slice(0, 4).map((course: any, idx: number) => (
                                <div key={course.id} className="flex items-center gap-6 px-6 py-4 hover:bg-slate-50 dark:hover:bg-white/5 transition-all group">
                                    <div className="size-10 rounded-lg bg-slate-100 dark:bg-white/5 flex items-center justify-center shrink-0 text-sm font-black text-slate-400">
                                        {idx + 1}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-black text-slate-900 dark:text-white group-hover:text-primary transition-colors truncate">{course.title}</p>
                                        <div className="flex items-center gap-4 mt-1.5">
                                            <p className="text-xs text-slate-500 dark:text-slate-400 font-bold flex items-center gap-1.5">
                                                <span className="material-symbols-outlined text-[14px]">group</span>
                                                {course.enrollmentCount || 0} Pendaftar
                                            </p>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 font-bold flex items-center gap-1.5 border-l border-slate-200 dark:border-white/10 pl-4">
                                                <span className="material-symbols-outlined text-[14px]">sell</span>
                                                {course.price > 0 ? `Rp ${course.price.toLocaleString('id-ID')}` : 'Gratis'}
                                            </p>
                                        </div>
                                    </div>
                                    <Link
                                        to={`/admin/courses/${course.id}/builder`}
                                        className="size-8 rounded-lg bg-slate-100 dark:bg-white/5 text-slate-900 dark:text-white flex items-center justify-center hover:bg-primary hover:text-white transition-all shadow-sm"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">chevron_right</span>
                                    </Link>
                                </div>
                            ))
                        ) : (
                            <div className="p-10 text-center text-[11px] font-black text-slate-400">Belum ada kursus</div>
                        )}
                    </div>
                </div>

                {/* Recent Withdrawals */}
                <div className="rounded-xl bg-white dark:bg-surface-card border border-slate-100 dark:border-white/5 overflow-hidden shadow-sm">
                    <div className="px-6 py-4 border-b border-slate-100 dark:border-white/5 flex items-center justify-between bg-slate-50/30 dark:bg-white/[0.01]">
                        <div className="flex items-center gap-3">
                            <span className="material-symbols-outlined text-amber-500 text-[22px]">payments</span>
                            <h3 className="text-sm font-black text-slate-900 dark:text-white">Penarikan Menunggu</h3>
                        </div>
                        <Link to="/admin/withdrawals" className="text-sm font-black text-primary hover:underline flex items-center gap-1.5">
                            Semua
                            <span className="material-symbols-outlined text-[14px]">arrow_forward</span>
                        </Link>
                    </div>
                    <div className="divide-y divide-slate-50 dark:divide-white/5">
                        {isLoadingWithdrawals ? (
                            <div className="p-10 text-center text-[11px] font-bold text-slate-400">Memuat...</div>
                        ) : pendingWithdrawals && pendingWithdrawals.length > 0 ? (
                            pendingWithdrawals.slice(0, 4).map((withdrawal: any) => (
                                <div key={withdrawal.id} className="flex items-center gap-6 px-6 py-4 hover:bg-slate-50 dark:hover:bg-white/5 transition-all">
                                    <div className="size-10 rounded-lg bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center shrink-0">
                                        <span className="material-symbols-outlined text-amber-600 dark:text-amber-400 text-[20px]">person</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-black text-slate-900 dark:text-white truncate">{withdrawal.userName || 'Instruktur'}</p>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 font-black">Rp {(withdrawal.amount || 0).toLocaleString('id-ID')}</p>
                                    </div>
                                    <Link
                                        to={`/admin/withdrawals/${withdrawal.id}`}
                                        className="px-4 py-2 rounded-lg bg-amber-500 text-white text-xs font-black hover:bg-amber-600 transition-all shadow-lg shadow-amber-500/20"
                                    >
                                        Review
                                    </Link>
                                </div>
                            ))
                        ) : (
                            <div className="p-10 text-center text-[11px] font-black text-slate-400">Tidak ada penarikan</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AdminDashboardPage
